import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import ChainedBackend from 'i18next-chained-backend'
import LanguageDetector from 'i18next-browser-languagedetector'
import I18NextHttpBackend from 'i18next-http-backend'
import I18NextLocalStorageBackend from 'i18next-localstorage-backend'
import { SUPPORTED_LOCALES, VALUE_FORMAT_TYPES } from './constants'
import { customDateFormatFunctions } from '../utilities/date'
import { REACT_APP_I18N_SAVE_MISSING } from '../../config/runtimeEnv'
import { IS_DEV_ENV, IS_TEST_ENV } from '../../constants/general'

/** Avoid parallel `.init()` calls (e.g. Vite HMR) before `isInitialized` flips. */
let initPromise = null

/** With `load: 'languageOnly'`, i18n passes short codes (`en`, `fr`) into interpolation; date helpers use regional keys. */
const resolveLngForDateFormats = lng => {
  if (!lng || typeof lng !== 'string') return SUPPORTED_LOCALES.EN_GB
  const lower = lng.toLowerCase()
  if (lower === 'en' || lower.startsWith('en-')) return SUPPORTED_LOCALES.EN_GB
  if (lower === 'fr' || lower.startsWith('fr-')) return SUPPORTED_LOCALES.FR
  if (lower === 'es' || lower.startsWith('es-')) return SUPPORTED_LOCALES.ES
  if (lower === 'de' || lower.startsWith('de-')) return SUPPORTED_LOCALES.DE
  if (lower === 'it' || lower.startsWith('it-')) return SUPPORTED_LOCALES.IT
  return lng
}

/**
 * i18next-fs-backend pulls `node:fs` — only dynamically import it in the Node test runner so Vite's browser bundle
 * never resolves `node:fs` (see Vite "externalized for browser compatibility" warnings).
 */
const buildChainedBackendOptions = async () => {
  const localOptions = {
    expirationTime: IS_DEV_ENV
      ? 0
      : 7 * 24 * 60 * 60 * 1000
  }

  if (IS_TEST_ENV) {
    const { default: I18NextFSBackend } = await import('i18next-fs-backend')
    return {
      backends: [I18NextLocalStorageBackend, I18NextFSBackend],
      backendOptions: [
        localOptions,
        { loadPath: '/public/locales/{{lng}}/{{ns}}.json' }
      ]
    }
  }

  return {
    backends: [I18NextLocalStorageBackend, I18NextHttpBackend],
    backendOptions: [
      localOptions,
      {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
        crossDomain: true,
        addPath: IS_DEV_ENV
          ? 'http://localhost:3001/locales/add/{{lng}}/{{ns}}'
          : undefined
      }
    ]
  }
}

const initIntl = () => {
  if (i18n.isInitialized) {
    return Promise.resolve(i18n)
  }
  if (initPromise) {
    return initPromise
  }

  initPromise = (async () => {
    const { backends, backendOptions } = await buildChainedBackendOptions()
    await i18n
      .use(ChainedBackend)
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        // Verbose i18next logging only when explicitly enabled (reduces dev console noise).
        debug: process.env.REACT_APP_I18N_DEBUG === 'true',
        // Locales are loaded by language code (en/fr/es/de/it) from `public/locales/{{lng}}/translation.json`.
        load: 'languageOnly',
        supportedLngs: ['en', 'fr', 'es', 'de', 'it', 'cimode'],
        fallbackLng: 'en',
        // Opt-in: saveMissing + chained backends is discouraged unless the save service is running (npm run i18n:start).
        saveMissing: REACT_APP_I18N_SAVE_MISSING === 'true',
        defaultNS: 'translation',
        interpolation: {
          escapeValue: false,
          format: (value, format, lng) => {
            if (value instanceof Date) {
              const formatterName = `${format}_${resolveLngForDateFormats(lng)}`
              const formatter = customDateFormatFunctions[formatterName]
              if (!formatter && process.env.NODE_ENV === 'development') {
                console.warn(`[Date value not formatted]: No formatter function was found with the name: "${formatterName}". 
You can implement the formatter function by adding a new prop to the variable "customDateFormatFunctions" inside "src/utilities/date.js".
The naming convention you have to follow is {formatType}_{language}.`)
                return value
              }

              return formatter(value)
            } else {
              if (format === VALUE_FORMAT_TYPES.TWO_DIGITS_FIXED_FLOAT_NUMBER) {
                return parseFloat(value).toFixed(2)
              }
            }

            return value
          }
        },
        backend: {
          backends,
          backendOptions
        }
      })
    return i18n
  })()
    .finally(() => {
      initPromise = null
    })

  return initPromise
}

export default initIntl
